import { ComponentChildren, FunctionalComponent } from 'preact'
import { z } from 'zod'
import { ModulesContext } from '../../utils/globals'
import classnames from 'classnames'

export interface WidgetContainerProps {
  widgetName: string
  config: WidgetContainerConfig
  noSpacing?: boolean
  noSideMargins?: boolean
  alwaysAddPadding?: boolean
  children: ComponentChildren
}

export const widgetContainerConfig = z.object({
  title: z.union([z.string(), z.undefined()]),
  subtitle: z.union([z.string(), z.undefined()]),
  backgroundColor: z.union([z.string(), z.undefined()]),
  backgroundColorDisplay: z.union([z.literal('solid_color'), z.literal('none'), z.undefined()]),
  titleColor: z.union([z.string(), z.undefined()]),
  titleColorDisplay: z.union([
    z.literal('solid_color'),
    z.literal('config-header-color'),
    z.literal('config--main-color-night'),
    z.undefined()
  ]),
  descriptionColor: z.union([z.string(), z.undefined()]),
  descriptionColorDisplay: z.union([
    z.literal('solid_color'),
    z.literal('config-header-color'),
    z.literal('config--main-color-night'),
    z.undefined()
  ])
})

export type WidgetContainerConfig = z.infer<typeof widgetContainerConfig>

const WidgetContainer: FunctionalComponent<WidgetContainerProps> = ({
  config,
  widgetName,
  children,
  alwaysAddPadding,
  noSideMargins,
  noSpacing
}) => {
  return (
    <ModulesContext.Consumer>
      {(modules) => {
        const isFeatureFlagEnabled = modules.includes('subtitleAndBackgroundToWidgets')
        const isCustomBackgroundColor = config.backgroundColorDisplay === 'solid_color'
        const style =
          isFeatureFlagEnabled && isCustomBackgroundColor
            ? { backgroundColor: config.backgroundColor }
            : undefined

        return (
          <section
            className={classnames(
              widgetName,
              `qa-${widgetName}-container`,
              'homepage-widget-wrapper',
              {
                'widget-container-spacing': alwaysAddPadding ?? isCustomBackgroundColor,
                'homepage-widget-wrapper--no-side-margins': noSideMargins,
                'homepage-widget-wrapper--no-spacing': noSpacing
              }
            )}
            style={style}
          >
            <div className='sitewidth'>
              <div className='col'>
                {config.title && (
                  <h3 className={`widget__heading ${widgetName}__heading`}>{config.title}</h3>
                )}
                {config.subtitle && isFeatureFlagEnabled && (
                  <h4 className={`widget__subheading ${widgetName}__subheading font-normal-weight`}>
                    {config.subtitle}
                  </h4>
                )}
                {children}
              </div>
            </div>
          </section>
        )
      }}
    </ModulesContext.Consumer>
  )
}

export default WidgetContainer
